/**
 * „Masz niezapisany program" — what „+ Nowy program" asks first when unsaved work is still around
 * (owner, 23 September 2026). Continue it, or start from scratch and lose it.
 *
 * ── `Dialog`, not `AlertDialog` ──────────────────────────────────────────────────────────────
 * Dismissing it — Escape, the backdrop, „Zamknij" — does NOTHING: the leftover plan stays where it
 * is and no create modal opens. That is a safe outcome, so the dialog may be dismissed the ordinary
 * way, and `shared/ui/ConfirmDialog`'s forced answer would be the wrong shape.
 *
 * ── Focus lands on „Kontynuuj" ───────────────────────────────────────────────────────────────
 * The opposite of `ConfirmDialog`'s rule, for the opposite reason: there, the reflexive Enter must
 * commit nothing, and here the reflexive Enter KEEPS the work — the one answer that loses anything
 * is the one that is not focused. „Zacznij od nowa" asks no second question; its consequence is
 * stated beside it and named by it (`aria-describedby`).
 *
 * ── Where focus goes when it closes depends on the answer ────────────────────────────────────
 * Dismissed, it returns to the control that opened it. After „Zacznij od nowa" the create modal is
 * opening in its place and takes focus to its own heading; returning focus to the „+" once this
 * one's exit has played would pull it back out of the new dialog. After „Kontynuuj" the route
 * changes and the shell moves focus to the builder's heading.
 *
 * ── „Zacznij od nowa" hands over under ONE scrim ─────────────────────────────────────────────
 * The create window opens only once this one's exit has finished (`onOpenChangeComplete`). Both
 * wear the same scrim (`shared/ui/splitDialog`): this one holds it at full strength through its
 * exit, the create window's arrives already at full strength, and its window rises in on top.
 * Opening both at once drew two windows over each other; two different scrims snapped the blur on
 * and un-dimmed the rail at the swap.
 *
 * ── The entrance is the shared `rise` ───────────────────────────────────────────────────────
 * The card fades and settles in CSS (Base UI drives it through `data-starting-style`), and its rows
 * follow with `shared/design-system/motion`'s `rise` cascade, `reducedMotion` passed in.
 *
 * ── The popup is always in the tree ──────────────────────────────────────────────────────────
 * Only its CONTENT waits for a plan. A popup rendered conditionally strands its backdrop: Base UI
 * waits on the popup element's animations to unmount, and a null element never reports finishing.
 */

import { useId, useRef, type RefObject } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import type { LeftoverPlan } from '~/entities/plan-draft';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import surface from '~/shared/ui/dialogSurface.module.css';
import split from '~/shared/ui/splitDialog.module.css';

import { LeftoverPlanCard } from './LeftoverPlanCard';
import card from './LeftoverPlanCard.module.css';
import styles from './ResumePlanDialog.module.css';

interface ResumePlanDialogProps {
  readonly open: boolean;
  /**
   * The plan on offer. Kept by the caller after the dialog closes, so the exit animation plays over
   * the content the trainer was reading rather than over an empty box.
   */
  readonly plan: LeftoverPlan | null;
  /**
   * „Zacznij od nowa" was pressed: the create window takes this one's place once its exit has
   * played, so the scrim stays at full strength through the exit instead of fading out and
   * snapping back under the next window.
   */
  readonly handingOver: boolean;
  readonly onContinue: () => void;
  readonly onStartFresh: () => void;
  readonly onDismiss: () => void;
  /** The exit has finished — the moment a hand-over may open the next window. */
  readonly onClosed: () => void;
  /**
   * Where a DISMISS returns focus — the control that opened it, as the create window takes it: a
   * ref, or a callback resolved at close time. See the header for why the other answers do not.
   */
  readonly finalFocus: RefObject<HTMLElement | null> | (() => HTMLElement | null);
}

type Answer = 'dismiss' | 'continue' | 'startFresh';

export function ResumePlanDialog({
  open,
  plan,
  handingOver,
  onContinue,
  onStartFresh,
  onDismiss,
  onClosed,
  finalFocus,
}: ResumePlanDialogProps) {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const primary = useRef<HTMLButtonElement>(null);
  const bodyId = useId();
  const cardId = useId();
  const noteId = useId();
  // Read by `finalFocus` after the close, long after the click that set it. Written in the click
  // handler, never during render.
  const answer = useRef<Answer>('dismiss');
  /** The shared `rise` cascade, row by row — the app's one entrance vocabulary. */
  const rise = (index: number) => screenMotion('rise', { reducedMotion, index });

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        answer.current = 'dismiss';
        onDismiss();
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) onClosed();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop
          className={[split.backdrop, handingOver ? styles.holdScrim : ''].filter(Boolean).join(' ')}
        />
        <Dialog.Popup
          className={`${String(surface.popup)} ${String(styles.popup)}`}
          initialFocus={primary}
          finalFocus={() => {
            if (answer.current !== 'dismiss') return false;
            const opener = typeof finalFocus === 'function' ? finalFocus() : finalFocus.current;
            return opener ?? true;
          }}
          // The question, then WHICH plan it is about: the card's name, figures and age are read
          // with the dialog, so a screen reader user is not asked about a plan they cannot hear.
          aria-describedby={`${bodyId} ${cardId}`}
        >
          {plan !== null && (
            <>
              <motion.div className={styles.head} {...rise(0)}>
                <Dialog.Title className={surface.title}>{t('web:planResume.title')}</Dialog.Title>
                <p id={bodyId} className={surface.description}>
                  {t('web:planResume.body')}
                </p>
              </motion.div>
              <motion.div {...rise(1)}>
                <LeftoverPlanCard plan={plan} id={cardId} />
              </motion.div>
              <motion.p id={noteId} className={card.priceNote} {...rise(2)}>
                {t('web:planResume.startFreshNote')}
              </motion.p>
              <motion.div className={surface.actions} {...rise(3)}>
                <Dialog.Close className={surface.cancel}>{t('web:planResume.close')}</Dialog.Close>
                <button
                  type="button"
                  className={surface.dangerGhost}
                  aria-describedby={noteId}
                  onClick={() => {
                    answer.current = 'startFresh';
                    onStartFresh();
                  }}
                >
                  {t('web:planResume.startFresh')}
                </button>
                <button
                  ref={primary}
                  type="button"
                  className={surface.primary}
                  onClick={() => {
                    answer.current = 'continue';
                    onContinue();
                  }}
                >
                  {t('web:planResume.continue')}
                </button>
              </motion.div>
            </>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
